<script setup lang="ts">
import { LangType } from "@/helpers/types";
import { useI18n } from "vue-i18n";
import { ref, computed } from "vue";

const { locale } = useI18n();

const setLocale = (lang: string) => {
  locale.value = lang;
};

const { supportedLanguages } = defineProps<{
  supportedLanguages?: LangType[];
}>();

const showDropdown = ref(false);
const dropdownStyle = ref({});
const selectorRef = ref<HTMLElement | null>(null);

const languages = computed(() => {
  const langs = [];
  if (supportedLanguages?.includes(LangType.pt_BR)) {
    langs.push({ code: 'pt', flag: 'fi-br', label: 'Português' });
  }
  if (supportedLanguages?.includes(LangType.en_US)) {
    langs.push({ code: 'en', flag: 'fi-gb', label: 'English' });
  }
  if (supportedLanguages?.includes(LangType.es_ES)) {
    langs.push({ code: 'es', flag: 'fi-es', label: 'Español' });
  }
  return langs;
});

const currentLanguage = computed(() => {
  return languages.value.find(lang => lang.code === locale.value);
});

const toggleDropdown = (event: Event) => {
  showDropdown.value = !showDropdown.value;

  if (showDropdown.value && event.currentTarget) {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    dropdownStyle.value = {
      right: `${window.innerWidth - rect.right}px`,
    };
  }
};

const selectLanguage = (lang: { code: string; flag: string; label: string }) => {
  setLocale(lang.code);
  showDropdown.value = false;
};

</script>

<template v-if="supportedLanguages">
  <div class="language-selector" ref="selectorRef">
    <div class="selected-language" @click="toggleDropdown">
      <span v-if="currentLanguage" :class="['flag', currentLanguage.flag]"></span>
      <svg
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        :class="{ 'rotated': showDropdown }"
      >
        <path d="M1 1L6 6L11 1" stroke="#151f45" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <div v-if="showDropdown" class="dropdown-menu" :style="dropdownStyle">
      <div
        v-for="lang in languages"
        :key="lang.code"
        class="dropdown-item"
        @click="selectLanguage(lang)"
      >
        <span :class="['flag', lang.flag]"></span>
        <span class="language-label">{{ lang.label }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.language-selector {
  position: relative;
  display: inline-block;
  z-index: 9999;
}

.selected-language {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.selected-language:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.flag {
  width: 2rem;
  height: 1.5rem;
  display: inline-block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 3px;
}

.selected-language svg {
  transition: transform 0.3s;
}

.selected-language svg.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
    margin-top: 5px;

  position: fixed;
  background: lightgrey;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 99999;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
    background: grey;

}

.language-label {
  font-size: 0.95rem;
  color: #151f45;
  font-weight: 400;
}
</style>
